import React, { useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getPlan } from "../../api";
import { Plan } from "../../@types";
import {
  AppPage,
  ReferrerContext,
  EnrollmentDownAlert,
  LoadingMask,
} from "../../components";
import {
  logError,
  parsePlanLongIdIntoUrlPartForApi,
  useTranslate,
} from "../../helpers";
import { PlanDetailsErrorPage } from "./ErrorPage";

import { AppStore } from "../../app/store";
import { PlanDetailsCostsContainer } from "./components";
import { BackLinkAndPrint } from "./BackLinkAndPrint";
import { AlertMessageProvider } from "../../app/contexts";
import { ApiError } from "../../helpers/errors";

interface PlanDetailsMatchParams {
  id: string;
}

enum PlanDetailsStatus {
  None,
  // loading plan data
  Loading,
  // successfully loaded plan data
  Success,
  // failed to load plan data
  Error,
}
interface PlanDetailsState {
  status: PlanDetailsStatus;
  plan: Plan | undefined;
  error?: ApiError;
}

export const PlanDetailsPage: React.FunctionComponent<RouteComponentProps> = ({
  location,
  match,
}) => {
  const t = useTranslate();
  const [planDetailState, setPlanDetailState] = React.useState<
    PlanDetailsState
  >({
    status: PlanDetailsStatus.None,
    plan: undefined,
  });

  const {
    state: {
      lis,
      compareList,
      year,
      futureLis,
      currentCoverage,
      nextYearCoverage,
    },
  } = React.useContext(AppStore.AppContext);

  const { mctCurrentPlanYear = "" } = useFlags();

  const lisToSend =
    year !== mctCurrentPlanYear.toString() && futureLis ? futureLis : lis;

  const fetchPlan = async (planId: string): Promise<void> => {
    setPlanDetailState({
      status: PlanDetailsStatus.Loading,
      plan: undefined,
    });
    try {
      const fetchedPlan = await getPlan(
        planId,
        lisToSend ? { lis: lisToSend } : {}
      );
      setPlanDetailState({
        status: PlanDetailsStatus.Success,
        plan: fetchedPlan,
      });
    } catch (err) {
      setPlanDetailState({
        status: PlanDetailsStatus.Error,
        plan: undefined,
        error: err as ApiError,
      });
    }
  };

  useEffect(() => {
    const planId = parsePlanLongIdIntoUrlPartForApi(
      (match.params as PlanDetailsMatchParams).id
    );
    if (planId && planDetailState.status !== PlanDetailsStatus.Loading) {
      fetchPlan(planId);
    }
  }, []);

  if (planDetailState.status === PlanDetailsStatus.None) {
    return null;
  }

  if (planDetailState.status === PlanDetailsStatus.Error) {
    logError(
      "Failed to retrieve plan (PlanDetailsPage.v2)",
      planDetailState.error as Error
    );
    return <PlanDetailsErrorPage />;
  }

  const plan = planDetailState.plan as Plan;

  return (
    <AlertMessageProvider>
      <ReferrerContext.Consumer>
        {({ goBackTextId, icon }): JSX.Element => (
          <AppPage>
            <LoadingMask
              loading={planDetailState.status === PlanDetailsStatus.Loading}
              message={t("app.loading")}
            />
            <EnrollmentDownAlert
              className="ds-u-margin-bottom--3"
              plan={plan}
            />
            <BackLinkAndPrint
              search={location.search}
              goBackTextId={goBackTextId}
              icon={icon}
              compareList={compareList}
              currentCoverage={currentCoverage}
              nextYearCoverage={nextYearCoverage}
            />
            {planDetailState.plan && (
              <div className="e2e-plan-details-page mct-p-plan-details">
                <PlanDetailsCostsContainer plan={plan} />
              </div>
            )}
          </AppPage>
        )}
      </ReferrerContext.Consumer>
    </AlertMessageProvider>
  );
};
